import { useCallback } from 'react';
import { realtimerAudiences } from 'realtimer/realtimerAudiences';
import { useHubJobOrderLazyQuery } from 'shared/hooks/api/jobOrder/jobOrder.generated';
import { useAuthStore } from 'shared/hooks/stores/useAuthStore';
import { useJobOrdersStore } from 'shared/hooks/stores/useJobOrdersStore';
import { useRealtimer } from 'shared/hooks/useRealtimer';
import { endedApplicationProcessStatuses } from 'types/enums/job-order-application-process-status.enum';
import { JobOrderListItem } from 'types/JobOrderListItem/JobOrderListItem';
import { mapDtoToJobOrderListItem } from 'types/JobOrderListItem/mappers';

const sortListByCreatedAt = (a: JobOrderListItem, b: JobOrderListItem) =>
  a.createdAt > b.createdAt ? 1 : -1;

export const useApplicationStatusRealtimer = () => {
  const expertId = useAuthStore((state) => state.user?.expertId)!;
  const updateListItem = useJobOrdersStore((store) => store.updateListItem);
  const moveListItem = useJobOrdersStore((store) => store.moveListItem);
  const getListNamesById = useJobOrdersStore((store) => store.getListNamesById);

  const [getJobOrder] = useHubJobOrderLazyQuery({
    fetchPolicy: 'no-cache',
  });

  const handleUpdatedJobOrder = useCallback(async (jobOrderId: string) => {
    if (!jobOrderId) {
      return;
    }
    const res = await getJobOrder({
      variables: {
        jobOrderId,
      },
    });
    const jobOrder = res.data?.jobOrder;
    if (!jobOrder) {
      return;
    }
    const mappedJobOrderListItem = mapDtoToJobOrderListItem(jobOrder);
    updateListItem(jobOrderId, mappedJobOrderListItem);

    if (!mappedJobOrderListItem.application?.processStatus) {
      return;
    }
    if (
      endedApplicationProcessStatuses.includes(mappedJobOrderListItem.application.processStatus)
    ) {
      const isJobOrderOnActiveList = getListNamesById(jobOrderId).includes('activeApplied');
      if (isJobOrderOnActiveList) {
        moveListItem('activeApplied', 'endedApplied', jobOrderId, (arr) =>
          arr.sort(sortListByCreatedAt),
        );
      }
    }

    if (
      !endedApplicationProcessStatuses.includes(mappedJobOrderListItem.application.processStatus)
    ) {
      const isJobOrderOnEndedList = getListNamesById(jobOrderId).includes('endedApplied');
      if (isJobOrderOnEndedList) {
        moveListItem('endedApplied', 'activeApplied', jobOrderId, (arr) =>
          arr.sort(sortListByCreatedAt),
        );
      }
    }
  }, []);

  useRealtimer(
    realtimerAudiences.jobOrderApplicationStatusUpdated(expertId),
    (message: { jobOrderId: string }) => {
      handleUpdatedJobOrder(message.jobOrderId);
    },
  );
};
